import { type FC, type ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { HeaderCellTitle } from "~/common/components/Table/TableElements";
import { type OrderbookRowItem, type orderColumnPreferences } from "~/features/orderbook/models/orderbookModels";
import { PopperTooltip } from "~/common/components/Tooltip";
import { isMultiAccountOrderSearchEnabled } from "~/utils/features";
import { useTranslation } from "react-i18next";
import { type ColumnDef } from "@tanstack/react-table";
import { userIsDistributor, userIsPurchaser } from "~/domains/user/utils/roles";
import { type ColumnData } from "~/features/search/components/ProductResults/views/NewTabularView/ConfigureColumnsButton/ColumnConfigurator";
import {
  ActionButtonCell,
  columnSizes,
  DeliveryCell,
  NetPriceCell,
  NetValueCell,
  PackagingCell,
  ProductCell,
  PurchaseOrderCell,
  QuantityCell,
  RequestedDeliveryCell,
  SalesOrderCell,
  SchaefflerIdCell,
} from "./OrderbookResultCells";
import { sortConfirmedDeliveryDate, sortRequestedDeliveryDate } from "./OrderbookSorting";
import { Card, Stack } from "~/common/mui";
import { theme } from "~/app/theme";
import { NewOrderBookTable } from "./OrderbookTableStyles";
import { getDefaultColumnConfig } from "./ExportOrderbookColumnConfig";
import { parsePreferences } from "./OrderBookUtility";
import { OrderbookConfigureColumns } from "./OrderbookConfigureButton";
import ServiceCreationFlow from "../ServiceCreationFlow/ServiceCreationFlow";

const SORTING_ICON_SIZE = 22;

const headerTextStyle = {
  color: "var(--c-on-surface) !important",
  fontSize: "12px",
  lineHeight: "16px",
  letterSpacing: "0.4px",
} as const;

const subHeaderTextStyle = {
  color: "var(--c-on-surface-low-emphasis) !important",
  fontSize: "12px",
  lineHeight: "16px",
  letterSpacing: "0.4px",
} as const;

interface Props {
  rows: OrderbookRowItem[];
  disableSorting: boolean;
  orderBookColumnPreferences: orderColumnPreferences[];
}

const ORDERBOOK_ROW_HEIGHT = 48;

const OrderbookResultsTable: FC<Props> = (props) => {
  const { t } = useTranslation();
  const [openServiceCaseListDialog, setOpenServiceCaseListDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<OrderbookRowItem | null>(null);

  const showAccountColumn = isMultiAccountOrderSearchEnabled();
  const sortingEnabled = !props.disableSorting;

  const headerT = useCallback((key: string) => t(`page.abmonitor.tableHeader.${key}`), [t]);

  // Column config state controls which columns are rendered
  const [columnConfig, setColumnConfig] = useState<ColumnData[]>(() =>
    getDefaultColumnConfig(headerT, parsePreferences(props.orderBookColumnPreferences)),
  );

  useEffect(() => {
    const parsed = parsePreferences(props.orderBookColumnPreferences);
    setColumnConfig(getDefaultColumnConfig(headerT, parsed));
  }, [props.orderBookColumnPreferences, headerT]);

  const sortingTooltip = t("spinner.text");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const wrapIfSortingDisabled = (node: ReactNode) =>
    sortingEnabled ? node : <PopperTooltip tooltipContent={sortingTooltip}>{node}</PopperTooltip>;

  const isDistributorOrPurchaser = userIsDistributor() || userIsPurchaser();

  // keep account column visibility in sync with feature flag
  useEffect(() => {
    setColumnConfig((prev) =>
      prev.map((c) => (c.code === "account" ? { ...c, visible: c.visible && showAccountColumn } : c)),
    );
  }, [showAccountColumn]);

  const columns = useMemo<ColumnDef<OrderbookRowItem>[]>(() => {
    const commonSortableMeta = {
      className: "cellInSortableColumn",
      sx: {
        paddingLeft: `calc(8px + ${SORTING_ICON_SIZE}px) !important`,
        [theme.breakpoints.up("desktop-s")]: {
          paddingLeft: `calc(8px + ${SORTING_ICON_SIZE}px) !important`,
        },
        [theme.breakpoints.up("desktop-m")]: {
          paddingLeft: `calc(8px + ${SORTING_ICON_SIZE}px) !important`,
        },
      },
    };

    const baseColumns: ColumnDef<OrderbookRowItem>[] = [
      {
        id: "salesOrderNo",
        accessorFn: (row) => row.schaefflerOrderNo,
        header: () =>
          wrapIfSortingDisabled(
            <Stack data-test-id="abmonitor-sort-so-no">
              <HeaderCellTitle
                sx={headerTextStyle}
                header={headerT("salesOrderNo")}
                subheader={headerT("orderLineNo")}
              />
            </Stack>,
          ),
        cell: ({ row }) => <SalesOrderCell item={row.original} />,
        enableSorting: sortingEnabled,
        sortingFn: (a, b) => a.original.schaefflerOrderNo.localeCompare(b.original.schaefflerOrderNo),
        size: columnSizes.salesOrder,
        meta: commonSortableMeta,
      },
      {
        id: "purchaseOrderNo",
        accessorFn: (row) => row.customerOrderNo,
        header: () =>
          wrapIfSortingDisabled(
            <Stack data-test-id="abmonitor-sort-po-no">
              <HeaderCellTitle sx={headerTextStyle} header={headerT("purchaseOrderNo")} subheader={undefined} />
            </Stack>,
          ),
        cell: ({ row }) => <PurchaseOrderCell item={row.original} />,
        enableSorting: sortingEnabled,
        sortingFn: (a, b) => a.original.customerOrderNo.localeCompare(b.original.customerOrderNo),
        size: columnSizes.purchaseOrder,
        meta: commonSortableMeta,
      },
      {
        id: "productDesignation",
        accessorFn: (row) => row.product.designation,
        header: () =>
          wrapIfSortingDisabled(
            <Stack data-test-id="abmonitor-sort-designation">
              <HeaderCellTitle sx={headerTextStyle} header={headerT("product")} subheader={undefined} />
            </Stack>,
          ),
        cell: ({ row }) => <ProductCell item={row.original} t={t} />,
        enableSorting: sortingEnabled,
        sortingFn: (a, b) => a.original.product.designation.localeCompare(b.original.product.designation),
        size: columnSizes.product,
        meta: commonSortableMeta,
      },
      {
        id: "schaefflerId",
        accessorFn: (row) => row.product.sapMaterialNumber,
        header: () =>
          wrapIfSortingDisabled(
            <Stack data-test-id="abmonitor-sort-so-no">
              <HeaderCellTitle
                sx={headerTextStyle}
                subHeaderSx={subHeaderTextStyle}
                header={headerT("schaefflerId")}
                subheader={headerT("customerMatetialId")}
              />
            </Stack>,
          ),
        cell: ({ row }) => <SchaefflerIdCell item={row.original} t={t} />,
        enableSorting: sortingEnabled,
        sortingFn: (a, b) => a.original.product.sapMaterialNumber.localeCompare(b.original.product.sapMaterialNumber),
        size: columnSizes.salesOrder,
        meta: commonSortableMeta,
      },
      {
        id: "packaging",
        header: () => (
          <HeaderCellTitle
            sx={headerTextStyle}
            subHeaderSx={subHeaderTextStyle}
            header={headerT("packaging")}
            subheader={headerT("shipment")}
          />
        ),
        cell: ({ row }) => <PackagingCell item={row.original} t={t} />,
        enableSorting: false,
        size: columnSizes.packaging,
      },
      {
        id: "quantity",
        accessorFn: (row) => row.totalQuantity,
        header: () =>
          wrapIfSortingDisabled(
            <Stack data-test-id="abmonitor-sort-quantity">
              <HeaderCellTitle
                sx={headerTextStyle}
                subHeaderSx={subHeaderTextStyle}
                header={headerT("quantity")}
                subheader={headerT("previously")}
              />
            </Stack>,
          ),
        cell: ({ row }) => <QuantityCell item={row.original} t={t} />,
        enableSorting: sortingEnabled,
        sortingFn: (a, b) => a.original.curQuantity - b.original.curQuantity,
        size: columnSizes.quantity,
        meta: commonSortableMeta,
      },
      {
        id: "netPrice",
        header: () => (
          <HeaderCellTitle
            sx={headerTextStyle}
            subHeaderSx={subHeaderTextStyle}
            header={headerT("netPriceHeader")}
            subheader={headerT("priceUnit")}
          />
        ),
        cell: ({ row }) => <NetPriceCell item={row.original} t={t} />,
        enableSorting: false,
        size: columnSizes.netPrice,
      },
      {
        id: "netValue",
        header: () => <HeaderCellTitle sx={headerTextStyle} header={headerT("netValue")} subheader={undefined} />,
        cell: ({ row }) => <NetValueCell item={row.original} />,
        enableSorting: false,
        size: columnSizes.netValue,
      },
      {
        id: "deliveryDate",
        accessorFn: (row) => row.curDeliveryDate,
        header: () =>
          wrapIfSortingDisabled(
            <Stack data-test-id="abmonitor-sort-confirmed-delivery-date">
              <HeaderCellTitle
                sx={headerTextStyle}
                subHeaderSx={subHeaderTextStyle}
                header={headerT("deliveryDate")}
                subheader={headerT("previously")}
              />
            </Stack>,
          ),
        cell: ({ row }) => (
          <DeliveryCell item={row.original} t={t} isDistributorOrPurchaser={isDistributorOrPurchaser} />
        ),
        enableSorting: sortingEnabled,
        sortingFn: (a, b) => sortConfirmedDeliveryDate(a.original, b.original),
        size: columnSizes.deliveryDate,
        meta: commonSortableMeta,
      },
      {
        id: "requestedDeliveryDate",
        accessorFn: (row) => row.requestedDeliveryDate,
        header: () =>
          wrapIfSortingDisabled(
            <Stack data-test-id="abmonitor-sort-requested-delivery-date">
              <HeaderCellTitle sx={headerTextStyle} header={headerT("reqDeliveryDate")} subheader={undefined} />
            </Stack>,
          ),
        cell: ({ row }) => <RequestedDeliveryCell item={row.original} />,
        enableSorting: sortingEnabled,
        sortingFn: (a, b) => sortRequestedDeliveryDate(a.original, b.original),
        size: columnSizes.requestedDate,
        meta: commonSortableMeta,
      },
      {
        id: "actions",
        cell: ({ row }) => (
          <ActionButtonCell
            t={t}
            item={row.original}
            onClickServiceIcon={() => setOpenServiceCaseListDialog(true)}
            setSelectedOrder={(item: OrderbookRowItem) => setSelectedOrder(item)}
          />
        ),
        enableSorting: false,
        size: columnSizes.actions,
      },
    ];

    const visibleCodes: string[] = columnConfig.filter((c) => c.visible).map((c) => c.code);

    const codeToColumn = new Map(baseColumns.map((c) => [c.id, c] as const));
    return visibleCodes.map((code) => codeToColumn.get(code)).filter(Boolean) as ColumnDef<OrderbookRowItem>[];
  }, [columnConfig, headerT, t, sortingEnabled, isDistributorOrPurchaser, wrapIfSortingDisabled]);

  const fixedLeftColumn = ["salesOrderNo", "purchaseOrderNo"];
  const fixedRightColumn = ["actions"];
  const fixedColumn = [...fixedLeftColumn, ...fixedRightColumn];

  const pinningState = {
    columnPinning: {
      left: fixedLeftColumn,
      right: fixedRightColumn,
    },
  };

  return (
    <>
      <NewOrderBookTable
        data={props.rows}
        columns={columns}
        variant={"stripes"}
        fullwidth={false}
        stickyHeader={true}
        state={{
          ...pinningState,
        }}
        scrollbarOnTop={true}
        getRowId={(row) => row.id.toString()}
        virtualized={{
          estimateRowSize: (_index, row) => {
            if (!row) {
              return ORDERBOOK_ROW_HEIGHT;
            }

            return ORDERBOOK_ROW_HEIGHT;
          },
        }}
      />

      <Stack>
        <Card
          sx={{
            background: theme.palette.surface.main,
            borderRadius: "0 0 12px 12px",
            borderTop: "0",
            padding: "8px",
            position: "relative",
            top: "-24px",
          }}
        >
          <OrderbookConfigureColumns
            columns={columnConfig.filter((c) => !fixedColumn.includes(c.code))}
            setColumns={(action) => {
              setColumnConfig((prev) => {
                const updated = typeof action === "function" ? action(prev) : action;
                const fixed = prev.filter((c) => fixedColumn.includes(c.code));
                return [...fixed, ...updated];
              });
            }}
          />
        </Card>
      </Stack>
      {selectedOrder && (
        <ServiceCreationFlow
          openServiceCaseListDialog={openServiceCaseListDialog}
          onClose={() => setOpenServiceCaseListDialog(false)}
          soNumber={selectedOrder.schaefflerOrderNo}
          poNumber={selectedOrder.customerOrderNo}
          orderLineNo={selectedOrder.orderLineNo.split(":")[0]}
          subLineNo={selectedOrder.orderLineNo}
        />
      )}
    </>
  );
};

export default OrderbookResultsTable;
